import { PdfPageViewer } from '@tempots/beatui'
import {
  attr,
  computedOf,
  Fragment,
  html,
  MapSignal,
  on,
  prop,
  type TNode,
} from '@tempots/dom'
import type {
  PDFDocumentProxy,
  PDFPageProxy,
} from 'pdfjs-dist/types/src/display/api'
import type { WizardState } from '../wizard-state'
import type {
  ExtractedBand,
  ExtractedHeaderModel,
  ExtractedImage,
  ExtractedLine,
  Rect,
} from '../pdf-extract/types'
import {
  DESIGN_PAGE,
  extractLines,
  groupBlocks,
  linesInBand,
} from '../pdf-extract/text-extractor'
import { suggestBands } from '../pdf-extract/band-suggest'
import { classifyBands } from '../pdf-extract/classify'
import {
  cropRect,
  detectBackgroundShapes,
  detectLogoCandidates,
  renderPageToCanvas,
  sampleBandBackground,
  sampleBlockColor,
  type PageRaster,
} from '../pdf-extract/raster-sampler'

const FRAME_W = 560

/** One entry in the step's numbered task list (tasks work in any order). */
function task(num: string, title: string, ...body: TNode[]) {
  return html.div(
    attr.class('wiz-task'),
    html.div(attr.class('wiz-task-num'), num),
    html.div(
      attr.class('wiz-task-body'),
      html.div(attr.class('wiz-task-title'), title),
      ...body
    )
  )
}

export function MarkBandsStep(state: WizardState, agencyId: string) {
  void agencyId
  const pdfData = state.pdfData.value
  if (!pdfData) {
    state.step.set('upload')
    return html.span()
  }

  // Local step state (design coords unless noted).
  const headerY = prop(120)
  const footerOn = prop(false)
  const footerY = prop(DESIGN_PAGE.h - 90)
  const logoRects = prop<Rect[]>([])
  const drawingLogo = prop(false)
  const ready = prop(false)
  const working = prop(false)
  const error = prop<string | null>(null)
  /** Rects placed by auto-detection (by reference), for origin tagging. */
  const autoRects = new Set<Rect>()

  let allLines: ExtractedLine[] = []
  let warnings: string[] = []
  let page: PDFPageProxy | null = null
  let rasterPromise: Promise<PageRaster> | null = null
  /** Resolved raster, for the logo thumbnails in the task list. */
  let rasterRef: PageRaster | null = null
  /** Page height in design coords (≠ 1056 for A4-ratio sources). */
  let designPageH = DESIGN_PAGE.h

  const onLoadComplete = async ({ pdfDoc }: { pdfDoc: PDFDocumentProxy }) => {
    try {
      page = await pdfDoc.getPage(1)
      const [x0, y0, x1, y1] = page.view
      designPageH = (y1 - y0) * (DESIGN_PAGE.w / (x1 - x0))
      const content = await page.getTextContent()
      const result = extractLines(content, page)
      allLines = result.lines
      warnings = result.warnings
      const suggestion = suggestBands(allLines, designPageH)
      headerY.set(suggestion.headerY)
      if (suggestion.footerY != null) {
        footerOn.set(true)
        footerY.set(suggestion.footerY)
      }
      rasterPromise = renderPageToCanvas(page)
      ready.set(true)
      // Pre-box likely logos in the suggested bands (best-effort, async so
      // the step is interactive immediately). Seeds the same rects the
      // user can remove or redraw; skipped if they drew one first.
      void rasterPromise
        .then(raster => {
          rasterRef = raster
          if (logoRects.value.length > 0) {
            // User drew before the raster resolved: refresh so the task
            // list can render thumbnails for the existing boxes.
            logoRects.set([...logoRects.value])
            return
          }
          const bandRects: Rect[] = [
            {
              x: 0,
              y: 0,
              width: DESIGN_PAGE.w,
              height: Math.round(headerY.value),
            },
          ]
          if (footerOn.value) {
            bandRects.push({
              x: 0,
              y: Math.round(footerY.value),
              width: DESIGN_PAGE.w,
              height: Math.round(designPageH - footerY.value),
            })
          }
          const textRects = allLines.map(l => l.bbox)
          const found: Rect[] = []
          for (const band of bandRects) {
            const background = sampleBandBackground(raster, band, textRects)
            found.push(
              ...detectLogoCandidates(raster, band, textRects, background)
            )
          }
          if (found.length === 0 || logoRects.value.length > 0) return
          found.forEach(r => autoRects.add(r))
          logoRects.set(found)
        })
        .catch(() => {
          /* pre-boxing is best-effort; the user can always draw */
        })
    } catch (e) {
      console.error('extraction failed', e)
      error.set('We couldn’t read that document’s text.')
    }
  }

  /** Data-URL crop of a logo rect from the raster, for task-list thumbs. */
  const thumbUrl = (r: Rect): string | null => {
    if (!rasterRef) return null
    const s = rasterRef.pxPerDesign
    const sx = Math.max(0, Math.round(r.x * s))
    const sy = Math.max(0, Math.round(r.y * s))
    const sw = Math.min(rasterRef.canvas.width - sx, Math.round(r.width * s))
    const sh = Math.min(rasterRef.canvas.height - sy, Math.round(r.height * s))
    if (sw <= 0 || sh <= 0) return null
    const c = document.createElement('canvas')
    c.width = sw
    c.height = sh
    const ctx = c.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(rasterRef.canvas, sx, sy, sw, sh, 0, 0, sw, sh)
    return c.toDataURL()
  }

  const displayScale = FRAME_W / DESIGN_PAGE.w
  const toDisplay = (designY: number) => designY * displayScale
  const fromDisplayY = (px: number) =>
    Math.max(8, Math.min(designPageH - 8, px / displayScale))

  /** Assemble the ExtractedHeaderModel from the marked bands. */
  const finish = async () => {
    if (!ready.value || working.value) return
    working.set(true)
    error.set(null)
    try {
      const raster = rasterPromise ? await rasterPromise : null
      if (!raster) throw new Error('raster unavailable')

      // A4-ratio sources: footer content sits below the letter page bottom
      // in width-uniform design coords — shift the footer band up so it
      // hugs the letter page bottom, preserving its internal layout.
      const footerShift = Math.max(0, designPageH - DESIGN_PAGE.h)

      const bands: ExtractedBand[] = []
      const bandWarnings = [...warnings]

      const buildBand = (
        region: 'header' | 'footer',
        rect: Rect,
        shiftY: number
      ) => {
        const lines = linesInBand(allLines, rect)
        const blocks = groupBlocks(lines, rect)
        const imagesInBand = logoRects.value.filter(r => {
          const cy = r.y + r.height / 2
          return cy >= rect.y && cy <= rect.y + rect.height
        })
        const exclude = [...blocks.map(b => b.bbox), ...imagesInBand]
        // Partial-band background shapes (banner rectangles etc.) must be
        // detected BEFORE the band fill is sampled: a banner covering most
        // of the band would otherwise read as the band's own background,
        // and the design would get a full-bleed fill UNDER the banner
        // shape. With shape areas excluded, what remains is the true band
        // background (usually the paper → no fill at all).
        const shapes = detectBackgroundShapes(raster, rect, logoRects.value)
        const backgroundColor = sampleBandBackground(raster, rect, [
          ...exclude,
          ...shapes.map(s => s.bbox),
        ])
        for (const block of blocks) {
          block.colorHex = sampleBlockColor(raster, block, backgroundColor)
          // Text inside a marked logo area IS the logo (wordmarks, "HCS");
          // the crop already captures it, so a text block would duplicate
          // it. Pre-exclude — the review step can re-include.
          const cx = block.bbox.x + block.bbox.width / 2
          const cy2 = block.bbox.y + block.bbox.height / 2
          if (
            imagesInBand.some(
              r =>
                cx >= r.x &&
                cx <= r.x + r.width &&
                cy2 >= r.y &&
                cy2 <= r.y + r.height
            )
          ) {
            block.included = false
          }
        }
        if (blocks.length === 0 && imagesInBand.length === 0) {
          bandWarnings.push(`no-text-in-band:${region}`)
          return null
        }
        const shift = (r: Rect): Rect => ({ ...r, y: Math.round(r.y - shiftY) })
        const band: ExtractedBand = {
          region,
          bbox: shift(rect),
          backgroundColor,
          shapes: shapes.map(s => ({ ...s, bbox: shift(s.bbox) })),
          blocks: blocks.map(b => ({
            ...b,
            bbox: shift(b.bbox),
            lines: b.lines.map(l => ({ ...l, bbox: shift(l.bbox) })),
          })),
          images: [],
        }
        // Crops are taken from UNSHIFTED source coords; bboxes stored shifted.
        return { band, imagesInBand, shiftY }
      }

      const headerBuilt = buildBand(
        'header',
        { x: 0, y: 0, width: DESIGN_PAGE.w, height: Math.round(headerY.value) },
        0
      )
      const footerBuilt = footerOn.value
        ? buildBand(
            'footer',
            {
              x: 0,
              y: Math.round(footerY.value),
              width: DESIGN_PAGE.w,
              height: Math.round(designPageH - footerY.value),
            },
            footerShift
          )
        : null

      for (const built of [headerBuilt, footerBuilt]) {
        if (!built) continue
        for (const rectSrc of built.imagesInBand) {
          const crop = await cropRect(raster, rectSrc)
          const image: ExtractedImage = {
            bbox: { ...rectSrc, y: Math.round(rectSrc.y - built.shiftY) },
            blob: crop.blob,
            naturalWidth: crop.naturalWidth,
            naturalHeight: crop.naturalHeight,
            origin: autoRects.has(rectSrc) ? 'auto-detected' : 'user-crop',
          }
          built.band.images.push(image)
        }
        bands.push(built.band)
      }

      if (bands.length === 0) {
        state.importError.set(
          'We couldn’t find any header or footer content in the areas you marked.'
        )
        state.step.set('choose')
        return
      }

      const model: ExtractedHeaderModel = {
        source: 'deterministic-pdf',
        bands,
        suggestions: classifyBands(bands, state.profile.value),
        warnings: bandWarnings,
      }
      state.extraction.set(model)
      state.step.set('review')
    } catch (e) {
      console.error('band assembly failed', e)
      error.set(
        'Something went wrong analyzing the page — try adjusting the lines, or go back and try another file.'
      )
    } finally {
      working.set(false)
    }
  }

  // ── Divider + logo-rect interactions ─────────────────────────
  let dragTarget: 'header' | 'footer' | null = null
  let logoStart: { x: number; y: number } | null = null
  const logoDraft = prop<Rect | null>(null)

  const frameCoords = (e: PointerEvent, frame: HTMLElement) => {
    const r = frame.getBoundingClientRect()
    return {
      x: (e.clientX - r.left) / displayScale,
      y: (e.clientY - r.top) / displayScale,
    }
  }

  return html.div(
    attr.style('display:flex; gap:20px; align-items:flex-start;'),
    // ── Page frame ──────────────────────────────────────────────
    html.div(
      attr.class('wiz-page-frame'),
      attr.style(`width:${FRAME_W}px; flex-shrink:0; cursor:default;`),
      on.pointerdown((e: PointerEvent) => {
        const frame = (e.currentTarget as HTMLElement)!
        if (!drawingLogo.value) return
        const pos = frameCoords(e, frame)
        logoStart = pos
        logoDraft.set({ x: pos.x, y: pos.y, width: 0, height: 0 })
        frame.setPointerCapture(e.pointerId)
        e.preventDefault()
      }),
      on.pointermove((e: PointerEvent) => {
        const frame = (e.currentTarget as HTMLElement)!
        const pos = frameCoords(e, frame)
        if (dragTarget === 'header')
          headerY.set(
            fromDisplayY(e.clientY - frame.getBoundingClientRect().top)
          )
        else if (dragTarget === 'footer')
          footerY.set(
            fromDisplayY(e.clientY - frame.getBoundingClientRect().top)
          )
        else if (logoStart && logoDraft.value) {
          logoDraft.set({
            x: Math.round(Math.min(logoStart.x, pos.x)),
            y: Math.round(Math.min(logoStart.y, pos.y)),
            width: Math.round(Math.abs(pos.x - logoStart.x)),
            height: Math.round(Math.abs(pos.y - logoStart.y)),
          })
        }
      }),
      on.pointerup(() => {
        dragTarget = null
        if (logoStart && logoDraft.value) {
          const draft = logoDraft.value
          if (draft.width > 12 && draft.height > 12) {
            logoRects.set([...logoRects.value, draft])
          }
          logoStart = null
          logoDraft.set(null)
          drawingLogo.set(false)
        }
      }),
      PdfPageViewer({
        source: pdfData,
        page: 1,
        fit: 'width',
        renderTextLayer: false,
        renderAnnotationLayer: false,
        onLoadComplete,
      }),
      // Header shade + divider
      MapSignal(headerY, y =>
        Fragment(
          html.div(
            attr.class('wiz-band-shade'),
            attr.style(`top:0; height:${toDisplay(y)}px;`)
          ),
          html.div(
            attr.class('wiz-divider'),
            attr.style(`top:${toDisplay(y)}px;`),
            html.div(attr.class('wiz-divider-line')),
            html.div(
              attr.class('wiz-divider-handle'),
              on.pointerdown((e: PointerEvent) => {
                dragTarget = 'header'
                ;(e.currentTarget as HTMLElement)
                  .closest('.wiz-page-frame')
                  ?.setPointerCapture?.(e.pointerId)
                e.preventDefault()
                e.stopPropagation()
              }),
              'HEADER ends here — drag me'
            )
          )
        )
      ),
      // Footer shade + divider
      MapSignal(
        computedOf(footerOn, footerY)((onF, y) => ({ onF, y })),
        ({ onF, y }) =>
          onF
            ? Fragment(
                html.div(
                  attr.class('wiz-band-shade'),
                  attr.style(
                    `top:${toDisplay(y)}px; height:${Math.max(0, toDisplay(designPageH - y))}px;`
                  )
                ),
                html.div(
                  attr.class('wiz-divider'),
                  attr.style(`top:${toDisplay(y)}px;`),
                  html.div(attr.class('wiz-divider-line')),
                  html.div(
                    attr.class('wiz-divider-handle'),
                    attr.style('top:-13px;'),
                    on.pointerdown((e: PointerEvent) => {
                      dragTarget = 'footer'
                      ;(e.currentTarget as HTMLElement)
                        .closest('.wiz-page-frame')
                        ?.setPointerCapture?.(e.pointerId)
                      e.preventDefault()
                      e.stopPropagation()
                    }),
                    'FOOTER starts here — drag me'
                  )
                )
              )
            : html.span()
      ),
      // Committed logo rects + in-progress draft
      MapSignal(logoRects, rects =>
        Fragment(
          ...rects.map((r, i) =>
            html.div(
              attr.class('wiz-logo-rect'),
              attr.style(
                `left:${toDisplay(r.x)}px; top:${toDisplay(r.y)}px; width:${toDisplay(r.width)}px; height:${toDisplay(r.height)}px;`
              ),
              html.button(
                attr.type('button'),
                attr.class('wiz-logo-rect-remove'),
                attr.title('Remove'),
                on.click(() =>
                  logoRects.set(logoRects.value.filter((_, j) => j !== i))
                ),
                '×'
              )
            )
          )
        )
      ),
      MapSignal(logoDraft, draft =>
        draft
          ? html.div(
              attr.class('wiz-logo-rect'),
              attr.style(
                `left:${toDisplay(draft.x)}px; top:${toDisplay(draft.y)}px; width:${toDisplay(draft.width)}px; height:${toDisplay(draft.height)}px;`
              )
            )
          : html.span()
      )
    ),
    // ── Controls column: a numbered task list (any order works) ──
    html.div(
      attr.style('flex:1; display:flex; flex-direction:column; gap:16px;'),
      html.div(
        attr.class('wiz-hint'),
        MapSignal(state.pdfSourceName, n =>
          html.span(html.b('File: '), n || 'document')
        )
      ),
      html.div(
        attr.class('wiz-hint'),
        'Three quick things — in any order. The shaded areas are what we’ll rebuild.'
      ),
      task(
        '1',
        'Mark your header',
        html.div(
          attr.class('wiz-hint'),
          'Drag the “HEADER ends here” line until your whole header sits inside the top shaded area.'
        )
      ),
      task(
        '2',
        'Got a footer?',
        MapSignal(footerOn, onF =>
          Fragment(
            html.button(
              attr.type('button'),
              attr.class(
                onF ? 'wiz-btn wiz-btn-primary' : 'wiz-btn wiz-btn-secondary'
              ),
              on.click(() => footerOn.set(!footerOn.value)),
              onF ? 'This page has a footer ✓' : 'This page also has a footer'
            ),
            onF
              ? html.div(
                  attr.class('wiz-hint'),
                  'Drag the “FOOTER starts here” line until the footer sits inside the bottom shaded area.'
                )
              : html.span()
          )
        )
      ),
      task(
        '3',
        'Point out your logo',
        html.div(
          attr.class('wiz-hint'),
          'Anything you box is copied over as a picture, exactly as it looks — perfect for logos and graphics.'
        ),
        MapSignal(drawingLogo, drawing =>
          html.button(
            attr.type('button'),
            attr.class(
              drawing ? 'wiz-btn wiz-btn-primary' : 'wiz-btn wiz-btn-secondary'
            ),
            on.click(() => drawingLogo.set(!drawingLogo.value)),
            drawing
              ? 'Now drag a box around your logo…'
              : '🖼 Draw a box around your logo'
          )
        ),
        MapSignal(logoRects, rects => {
          const auto = rects.filter(r => autoRects.has(r)).length
          if (rects.length === 0) return html.span()
          return html.div(
            html.div(
              attr.class('wiz-logo-thumbs'),
              ...rects.map((r, i) => {
                const url = thumbUrl(r)
                return html.div(
                  attr.class('wiz-logo-thumb'),
                  url
                    ? html.img(
                        attr.src(url),
                        attr.alt('Marked logo area, as it will be copied')
                      )
                    : html.span(attr.class('wiz-hint'), '…'),
                  html.button(
                    attr.type('button'),
                    attr.class('wiz-logo-thumb-remove'),
                    attr.title('Remove this logo area'),
                    on.click(() =>
                      logoRects.set(logoRects.value.filter((_, j) => j !== i))
                    ),
                    '×'
                  )
                )
              })
            ),
            html.div(
              attr.class('wiz-hint'),
              auto > 0
                ? `We spotted ${auto === 1 ? 'what looks like your logo' : `${auto} likely logos`} — each is copied over exactly as shown. Remove (×) or redraw if we got it wrong.`
                : 'Each marked area is copied over exactly as shown — the × removes one.'
            )
          )
        })
      ),
      MapSignal(error, err =>
        err ? html.div(attr.class('wiz-error'), err) : html.span()
      ),
      MapSignal(
        computedOf(ready, working)((r, w) => ({ r, w })),
        ({ r, w }) =>
          html.button(
            attr.type('button'),
            attr.class('wiz-btn wiz-btn-primary'),
            attr.disabled(!r || w),
            on.click(() => void finish()),
            w
              ? 'Analyzing…'
              : r
                ? 'Looks right — continue →'
                : 'Reading document…'
          )
      )
    )
  )
}
